import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getQualityHealthScore, getQualityHistory, getQualityRun, runQualityNow,
} from "../api/adminApi";
import {
  StatCard, StatusBadge, Section, ShortId, fmtDate,
} from "../components/admin/AdminUI";
import ComparableQualityCard from "../components/admin/ComparableQualityCard";
import PriceActivityCard from "../components/admin/PriceActivityCard";

/**
 * Batch 4 Section F — Quality Dashboard.
 *
 * Surfaces the Regression Worker (Section E) output: overall health
 * score, per-check breakdown, history, and a manual "Run regression
 * now" button.
 *
 * Polling cadence:
 *   * idle:  60s
 *   * running (active run-now job): 5s until the run reaches a terminal
 *     state (healthy | degraded | critical), then back to 60s.
 *
 * No background workers in the UI — TanStack Query handles the refetch
 * interval and we just feed it the right number depending on `liveUlid`.
 */
export default function AdminQualityDashboardPage() {
  const qc = useQueryClient();
  // ulid of the currently-tracked "run-now" job (if any). When set we
  // poll the run/{ulid} endpoint at a fast cadence; once it terminates
  // we clear this and the regular 60s health-score polling resumes.
  const [liveUlid, setLiveUlid] = useState(null);

  // --- Latest run --------------------------------------------------------
  const healthQ = useQuery({
    queryKey: ["admin", "quality", "health-score"],
    queryFn: getQualityHealthScore,
    // 60s in idle state, paused while a live run is in flight (the
    // live-run query is the authoritative source until done).
    refetchInterval: liveUlid ? false : 60_000,
    refetchOnWindowFocus: !liveUlid,
  });

  // --- History list (compact) --------------------------------------------
  const historyQ = useQuery({
    queryKey: ["admin", "quality", "history"],
    queryFn: () => getQualityHistory(30),
    refetchInterval: liveUlid ? false : 60_000,
  });

  // --- Active run-now job (fast poll) ------------------------------------
  const liveQ = useQuery({
    queryKey: ["admin", "quality", "run", liveUlid],
    queryFn: () => getQualityRun(liveUlid),
    enabled: !!liveUlid,
    refetchInterval: 5_000,
  });

  // When the live run terminates, refresh health + history and clear
  // the liveUlid so the regular cadence resumes.
  useEffect(() => {
    const status = liveQ.data?.status;
    if (liveUlid && status && status !== "running") {
      setLiveUlid(null);
      qc.invalidateQueries({ queryKey: ["admin", "quality", "health-score"] });
      qc.invalidateQueries({ queryKey: ["admin", "quality", "history"] });
      toast.success(
        `Regression run abgeschlossen (${status}, ${liveQ.data?.overall_score?.toFixed(1) ?? "—"})`,
        { id: "regression-done" }
      );
    }
  }, [liveQ.data, liveUlid, qc]);

  // --- Run-Now mutation --------------------------------------------------
  const runMut = useMutation({
    mutationFn: runQualityNow,
    onSuccess: (data) => {
      setLiveUlid(data?.regression_run_ulid);
      toast.info(`Quality run queued: ${data?.regression_run_ulid?.slice(0, 8)}…`, {
        id: "regression-queued",
      });
    },
    onError: (e) => {
      toast.error(`Run-Now fehlgeschlagen: ${e?.message || e}`);
    },
  });

  const isAnyLoading = healthQ.isLoading || historyQ.isLoading;
  if (isAnyLoading && !healthQ.data && !historyQ.data) {
    return (
      <div className="text-zinc-500" data-testid="admin-quality-loading">
        Loading quality data…
      </div>
    );
  }

  if (healthQ.isError) {
    return (
      <div className="text-rose-400" data-testid="admin-quality-error">
        Error: {String(healthQ.error?.message || healthQ.error)}
      </div>
    );
  }

  const data = healthQ.data;
  const live = liveUlid ? liveQ.data : null;
  // Authoritative "current" doc: prefer the live one when present so the
  // dashboard updates immediately as the run finishes.
  const current = live && live.status !== "running" ? live : data;

  const hasRun = !!current?.has_run || !!(current?.ulid && current?.status);
  const runStatus = current?.status;
  const overallScore = current?.overall_score;
  const checks = current?.checks || [];
  const history = historyQ.data?.items || [];

  // Top-line semantic.
  const statusBadgeStyle = mapStatusToBadge(runStatus);

  return (
    <div data-testid="admin-quality-dashboard" className="space-y-8">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
            /api/admin/quality
          </span>
          <h1 className="text-2xl text-zinc-100 font-medium mt-1">
            Quality Dashboard
          </h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Daily regression checks: resolver accuracy, mapping coverage,
            taxonomy health, scheduler health, import success and
            dropdown-cascade sanity. Cron läuft täglich 03:00
            Europe/Vienna. Manueller Run unten verfügbar.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {liveUlid && (
            <div
              data-testid="quality-live-banner"
              className="px-3 py-1 rounded-md text-xs border border-sky-500/40 bg-sky-500/10 text-sky-200"
            >
              Run {liveUlid.slice(0, 8)}… is running…
            </div>
          )}
          <button
            data-testid="quality-run-now-btn"
            onClick={() => runMut.mutate()}
            disabled={runMut.isPending || !!liveUlid}
            className="px-4 py-2 rounded-md text-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {runMut.isPending ? "Queueing…" : "Run regression now"}
          </button>
        </div>
      </header>

      {/* Top stat row ------------------------------------------------- */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="quality-top-stats">
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-3" data-testid="quality-stat-status">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Health</div>
          <div className="text-2xl text-zinc-100 font-medium tabular-nums mt-1">
            {hasRun
              ? <span className={`px-2 py-0.5 rounded text-base uppercase tracking-wider border ${statusBadgeStyle}`}>
                  {runStatus}
                </span>
              : "—"}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {hasRun ? `as of ${fmtDate(current?.finished_at || current?.started_at)}` : "no runs yet"}
          </div>
        </div>
        <StatCard
          label="Overall score"
          value={overallScore != null ? overallScore.toFixed(1) : "—"}
          hint={overallScore != null ? "/ 100 (avg of checks)" : ""}
          testid="quality-stat-score"
        />
        <StatCard
          label="Checks"
          value={checks.length || 0}
          hint={
            checks.length
              ? `${checks.filter((c) => c.status === "pass").length} pass · ${checks.filter((c) => c.status === "warn").length} warn · ${checks.filter((c) => c.status === "fail").length} fail`
              : "—"
          }
          testid="quality-stat-checks"
        />
        <StatCard
          label="Last trigger"
          value={current?.trigger ? truncateTrigger(current.trigger) : "—"}
          hint={current?.duration_ms != null ? `${(current.duration_ms / 1000).toFixed(1)}s` : ""}
          testid="quality-stat-trigger"
        />
      </div>

      {/* Per-check grid ------------------------------------------------ */}
      <Section title="Checks" testid="quality-checks-section">
        {checks.length === 0 ? (
          <div className="text-xs text-zinc-500" data-testid="quality-checks-empty">
            Keine Checks zum Anzeigen — klicke „Run regression now“ um den ersten Lauf zu starten.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="quality-checks-grid">
            {checks.map((c) => <CheckCard key={c.name} check={c} />)}
          </div>
        )}
      </Section>

      {/* Batch 5a Section E — Comparable Quality card -------------------- */}
      <ComparableQualityCard />

      {/* Batch 5b-1 Section 4 — Price Activity card ---------------------- */}
      <PriceActivityCard />

      {/* History ------------------------------------------------------- */}
      <Section
        title="History (last 30 runs)"
        testid="quality-history-section"
        action={
          <span className="text-xs text-zinc-500">
            {history.length} run{history.length === 1 ? "" : "s"}
          </span>
        }
      >
        <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="quality-history-table">
          <table className="w-full text-xs">
            <thead className="bg-zinc-950/60 text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Score</th>
                <th className="text-right px-3 py-2 font-medium">Duration</th>
                <th className="text-left px-3 py-2 font-medium">Trigger</th>
                <th className="text-left px-3 py-2 font-medium">ULID</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-zinc-500 text-center">No runs yet.</td></tr>
              )}
              {history.map((row) => (
                <tr
                  key={row.ulid}
                  className="border-t border-zinc-900 hover:bg-zinc-900/40"
                  data-testid={`quality-history-row-${row.ulid}`}
                >
                  <td className="px-3 py-2 text-zinc-400">{fmtDate(row.started_at)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${mapStatusToBadge(row.status)}`}>
                      {row.status || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-200 tabular-nums">
                    {row.overall_score != null ? row.overall_score.toFixed(1) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-400 tabular-nums">
                    {row.duration_ms != null ? `${(row.duration_ms / 1000).toFixed(1)}s` : "—"}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 truncate max-w-[180px]" title={row.trigger}>
                    {truncateTrigger(row.trigger)}
                  </td>
                  <td className="px-3 py-2"><ShortId value={row.ulid} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

// --------------------------------------------------------------------------
// Per-check card. Two-state (collapsed default / expanded raw details).
// --------------------------------------------------------------------------
function CheckCard({ check }) {
  const [open, setOpen] = useState(false);
  const badgeCls = check.status === "pass"
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
    : check.status === "warn"
      ? "bg-amber-500/15 text-amber-200 border-amber-500/40"
      : "bg-rose-500/15 text-rose-300 border-rose-500/40";
  return (
    <div
      data-testid={`quality-check-${check.name}`}
      className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-mono text-zinc-200 truncate" title={check.name}>
          {check.name}
        </div>
        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${badgeCls}`}>
          {check.status}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="text-2xl tabular-nums text-zinc-100">
          {check.score != null ? check.score.toFixed(1) : "—"}
        </div>
        <div className="text-[10px] text-zinc-500">/100</div>
        {check.duration_ms != null && (
          <div className="ml-auto text-[10px] text-zinc-600 tabular-nums">
            {(check.duration_ms / 1000).toFixed(2)}s
          </div>
        )}
      </div>
      {/* Summary line — first detail key/value to give a one-line story. */}
      {check.details && Object.keys(check.details).length > 0 && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors"
          data-testid={`quality-check-${check.name}-toggle`}
        >
          {open ? "− Hide details" : "+ Show details"}
        </button>
      )}
      {open && (
        <pre
          data-testid={`quality-check-${check.name}-details`}
          className="text-[10px] text-zinc-400 bg-zinc-900/50 rounded p-2 overflow-x-auto max-h-48"
        >
          {JSON.stringify(check.details, null, 2)}
        </pre>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------
function mapStatusToBadge(status) {
  switch (status) {
    case "healthy":
      return "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
    case "degraded":
      return "bg-amber-500/20 text-amber-200 border-amber-500/40";
    case "critical":
      return "bg-rose-500/20 text-rose-300 border-rose-500/40";
    case "running":
      return "bg-sky-500/20 text-sky-300 border-sky-500/40";
    default:
      return "bg-zinc-700/30 text-zinc-400 border-zinc-700";
  }
}

function truncateTrigger(t) {
  if (!t) return "—";
  if (t.length <= 24) return t;
  // `manual:user@host.tld` → `manual:user@…`
  if (t.startsWith("manual:")) return `manual:${t.slice(7, 16)}…`;
  return `${t.slice(0, 22)}…`;
}
